import { Avatar, Card, CardActionArea, Chip, Dialog, Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import ReactJson from 'react-json-view';
import { fmtColor } from 'util/format';
import { ProductContentDialog } from './ProductContentDialog';
import { useTrackEvent } from 'util/trackEvent';
import { useRecoilState } from 'recoil';
import { autoPopupServiceState, bookingState, packagesState, servicesState } from 'pages/CustomerBookingCreate/state';
import { DialogCloseTransition } from 'components/DialogTransitions';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    height: 300*.6,
    width: 225*.7,
    flex: 'none',
    
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[10],
      transform: 'scale(1.05)',
    },
    cursor: 'pointer',
    overflow: 'auto',
    position: 'relative',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'flex-start',
    position: 'relative',
  },
  serviceIcon: {
    height: 315*.6,
    flex: 0,
    width: 'auto',
    alignSelf: 'flex-end',
    objectFit: "cover",
    objectPosition: "bottom",
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 0
  },
  serviceInfo: {
    flex: 1,
    alignSelf: 'normal',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: theme.spacing(1),
    paddingTop: theme.spacing(1),
    alignItems: 'flex-start',
    position: 'relative',

    "&>h6": {
      paddingBottom: theme.spacing(1.5)
    },
    zIndex: 1
  },
  serviceAction: {
    flex: 0,
    width: 75,
  },
  svcIcon: {
    height: theme.spacing(1.5),
    width: 'auto',
  },
  chip: {
    boxShadow: theme.shadows[1],
  },
  dialogPaper: {
    background: (props) => `linear-gradient(30deg, ${fmtColor(props?.header_start_color)} 0%, ${fmtColor(props?.header_end_color)} 100%)`
  },
  notAvailableBanner: {
    position: 'absolute',
    top: "50%",
    left: 0,
    right: 0,
    transform: "translate(0,-50%)",
    width: '100%',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&>span': {
      fontWeight: 'bolder',
    },
    textAlign: 'center',
    opacity: 0.8
  },
  selectedBanner: {
    position: 'absolute',
    top: "50%",
    left: 0,
    right: 0,
    transform: "translate(0,-50%)",
    width: '100%',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.contrastText,
    '&>span': {
      fontWeight: 'bolder',
    },
    textAlign: 'center',
    opacity: 0.8
  }

}));
const FeaturedCategoryCard = ({
  canAddToCart = true,
  category,
  onSelect = (card) => { }
}) => {
  const trackEvent = useTrackEvent()

  const [autoPopupService, setAutoPopupService] = useRecoilState(autoPopupServiceState);
  const [cartPackages, setCartPackages] = useRecoilState(packagesState);
  const [cartServices, setCartServices] = useRecoilState(servicesState);
  const [booking, setBooking] = useRecoilState(bookingState);
  const [openAuto, setOpenAuto] = useState(false);

  const styleProps = {
    header_start_color: category?.header_start_color,
    header_end_color: category?.header_end_color,
    header_text_color: category?.header_text_color,
  }
  const classes = useStyles(styleProps)
  const json = (
    <Card className={classes.root}>
      <ReactJson src={category?.fillOptions} collapsed sortKeys />
    </Card>
  )

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"), {
    noSsr: true,
  });

  const [showDialog, setShowDialog] = useState(false);

  useEffect(
    () => {
      setOpenAuto(autoPopupService?.rawService?.id === category?.id)
      setShowDialog(autoPopupService?.rawService?.id === category?.id)
    }, [autoPopupService]
  );

  const isInCart = useMemo(
    () => {
      const cartPackageIds = cartPackages?.map((p) => p?.id)
      const categoryPackageIds = category?.fillOptions?.prefill?.packages?.map((p) => p?.id)

      const cartServiceIds = cartServices?.map((s) => s?.id)
      const categoryServiceIds = category?.fillOptions?.prefill?.services?.map((s) => s?.id)

      if (cartPackageIds?.length > 0 && categoryPackageIds?.length > 0) {
        if (cartPackageIds?.every(i => categoryPackageIds?.includes(i))) {
          return true
        }
      } else if (cartServiceIds?.length > 0 && categoryServiceIds?.length > 0) {
        if (cartServiceIds?.every(i => categoryServiceIds?.includes(i))) {
          return true
        }
      } else if (
        //explicitly category only like lab tests
        cartPackageIds?.length <= 0 &&
        cartServiceIds?.length <= 0 &&
        categoryPackageIds?.length <= 0 &&
        categoryServiceIds?.length <= 0 &&

        category?.fillOptions?.prefill?.rawService?.featured_service?.type_id === (booking?.serviceCategory?.type?.id || booking?.serviceCategory?.featured_service?.type_id)) {
        return true
      }

      return false
    }, [cartPackages, cartServices, category, booking?.serviceCategory]
  );

  return (
    <>
      <Dialog
        maxWidth="sm"
        scroll='paper'
        fullWidth
        fullScreen={isMobile}
        open={showDialog}
        onClose={() => {
          trackEvent('Create Booking Featured Category Card - close')
          setShowDialog(false)
        }}
        TransitionComponent={DialogCloseTransition}
        classes={{
          paper: classes.dialogPaper
        }}
      >

        <ProductContentDialog
          canAddToCart={canAddToCart}
          inCart={openAuto}
          redirectLink={autoPopupService?.redirectStep}
          item={category}
          onSelect={async () => {
            trackEvent('Create Booking Featured Category Card - select')
            await onSelect(category)
            setShowDialog(false)
          }}
          onClose={() => setShowDialog(false)} />
      </Dialog>
      {/* {json} */}
      <Card
        className={classes.root}
        style={{
          background: `linear-gradient(0deg, ${category?.start_color ?? '#FFF'} 0%, ${category?.end_color ?? '#FFF'} 100%)`,
        }}>
        <CardActionArea onClick={() => {
          trackEvent('Create Booking Featured Category Card - open')
          setShowDialog(true)
        }} className={classes.container}>
          <img className={classes?.serviceIcon}
            src={`${category?.icon_url}`}
          />
          <div className={classes?.serviceInfo}>
            <Typography
              style={{
                color: category?.text_color
              }}
              variant='h5'>{category?.label}</Typography>
            <Typography
              style={{
                color: category?.text_color,
              }}
              variant='body2'>{category?.description}</Typography>
            <div style={{ flex: 1, }} />
            <Chip
              size="small"
              className={classes.chip}
              style={{
                color: category?.booking_type?.fg_color,
                backgroundColor: category?.booking_type?.bg_color,
              }}
              icon={
                <Avatar variant="square" className={classes.svcIcon} src={category?.booking_type?.icon_url} />
              }
              label={category?.booking_type?.label}
            />
            {(category?.not_available || category?.fillOptions?.disabled) && (
              <div className={classes.notAvailableBanner}>
                <Typography variant='button' >Not Available</Typography>
              </div>
            )}
            {(isInCart && !(category?.not_available || category?.fillOptions?.disabled)) && (
              <div className={classes.selectedBanner}>
                <Typography variant='button' >Selected</Typography>
              </div>
            )}
          </div>

        </CardActionArea>
      </Card>
    </>
  )
}


export { FeaturedCategoryCard };
